// ============================================================================
// Type Parser - Convert AST type nodes to structured Type objects
// ============================================================================

import {
  Type, Types, TypeAlias, TypeMemberAccess, KeyofType, UnionType, IntersectionType, ArrayType, ObjectType,
  TupleType, GenericType, LiteralType, PrimitiveType, PredicateType,
  StringType, NumberType, BooleanType, NullType, UndefinedType,
  AnyFunctionType, FunctionType
} from './Type.js';

/**
 * Parse an annotation node into a Type object
 * @param {Object} annotationNode - The annotation AST node
 * @returns {Type}
 */
export function parseAnnotation(annotationNode) {
  if (!annotationNode) return Types.any;

  // Predicate annotation: ': paramName is SomeType'
  if (annotationNode.named?.predicate_param) {
    const paramName = annotationNode.named.predicate_param.value;
    const guardType = parseTypeExpression(annotationNode.named.predicate_type);
    return new PredicateType(paramName, guardType);
  }

  // annotation.named.type is a type_expression
  if (annotationNode.named && annotationNode.named.type) {
    return parseTypeExpression(annotationNode.named.type);
  }

  // Old format fallback: annotation.named.name
  if (annotationNode.named && annotationNode.named.name) {
    const name = annotationNode.named.name.value;
    return primitiveFromName(name);
  }

  return Types.any;
}

/**
 * Parse a type_expression AST node into a Type object
 * Handles union (|), intersection (&), and delegates to type_primary
 * @param {Object} typeExprNode - The type_expression AST node
 * @returns {Type}
 */
export function parseTypeExpression(typeExprNode) {
  if (!typeExprNode) return Types.any;
  
  // Check for union type: type_primary | type_expression
  if (typeExprNode.named && typeExprNode.named.union) {
    const leftType = parseTypePrimary(typeExprNode.children[0]);
    const rightType = parseTypeExpression(typeExprNode.named.union);
    
    // Combine types into union
    const leftTypes = leftType instanceof UnionType ? leftType.types : [leftType];
    const rightTypes = rightType instanceof UnionType ? rightType.types : [rightType];
    
    return Types.union([...leftTypes, ...rightTypes]);
  }
  
  // Check for intersection type: type_primary & type_expression  
  if (typeExprNode.named && typeExprNode.named.intersection) {
    const leftType = parseTypePrimary(typeExprNode.children[0]);
    const rightType = parseTypeExpression(typeExprNode.named.intersection);
    
    // Combine types into intersection
    const leftTypes = leftType instanceof IntersectionType ? leftType.types : [leftType];
    const rightTypes = rightType instanceof IntersectionType ? rightType.types : [rightType];
    
    return Types.intersection([...leftTypes, ...rightTypes]);
  }
  
  // Just a single type_primary
  if (typeExprNode.children && typeExprNode.children[0]) {
    return parseTypePrimary(typeExprNode.children[0]);
  }
  
  return Types.any;
}

/**
 * Parse a type_primary AST node into a Type object
 * Handles basic types, array types, object types, literal types, and generic type instantiation
 * @param {Object} typePrimaryNode - The type_primary AST node
 * @returns {Type}
 */
export function parseTypePrimary(typePrimaryNode) {
  if (!typePrimaryNode || !typePrimaryNode.named) return Types.any;
  
  // Check for function type: (params) => ReturnType
  if (typePrimaryNode.named.return) {
    const returnType = parseTypeExpression(typePrimaryNode.named.return);
    const paramTypes = [];
    const paramNames = [];

    function collectFuncTypeParams(node) {
      if (!node || !node.named) return;
      if (node.named.name) paramNames.push(node.named.name.value);
      if (node.named.type) paramTypes.push(parseTypeExpression(node.named.type));
      if (node.named.rest) collectFuncTypeParams(node.named.rest);
    }

    collectFuncTypeParams(typePrimaryNode.named.params || null);
    return new FunctionType(paramTypes, returnType, [], paramNames);
  }

  // Check for tuple type: [string, number, boolean]
  if (typePrimaryNode.named.tuple) {
    const tupleNode = typePrimaryNode.named.tuple;
    const elements = parseTupleTypeElements(tupleNode.named?.elements);
    const baseType = new TupleType(elements);
    // Handle optional array suffix: [string, number][]
    const arraySuffixNode = typePrimaryNode.children?.find(child => child.type === 'array_suffix');
    if (arraySuffixNode) {
      return parseArraySuffix(baseType, arraySuffixNode);
    }
    return baseType;
  }

  // Check for keyof type: keyof T
  if (typePrimaryNode.named.subject) {
    const subjectType = parseTypePrimary(typePrimaryNode.named.subject);
    const baseType = new KeyofType(subjectType);
    const arraySuffixNode = typePrimaryNode.children?.find(child => child.type === 'array_suffix');
    if (arraySuffixNode) {
      return parseArraySuffix(baseType, arraySuffixNode);
    }
    return baseType;
  }

  let baseType = Types.any;
  
  // Check for object type
  if (typePrimaryNode.children && typePrimaryNode.children[0] && 
      typePrimaryNode.children[0].type === 'object_type') {
    baseType = parseObjectType(typePrimaryNode.children[0]);
  } else
  
  // Check for string literal type
  if (typePrimaryNode.named.literal && typePrimaryNode.named.literal.type === 'str') {
    // Strip quotes from the token value
    const rawValue = typePrimaryNode.named.literal.value.slice(1, -1);
    baseType = Types.literal(rawValue, StringType);
  } else
  
  // Check for number literal type
  if (typePrimaryNode.named.literal && typePrimaryNode.named.literal.type === 'number') {
    const value = parseFloat(typePrimaryNode.named.literal.value);
    baseType = Types.literal(value, NumberType);
  } else {
    const { name, type_args, member, member_key } = typePrimaryNode.named;
    if (name) {
      // name is a type_name node, get its first child
      const typeToken = name.children ? name.children[0] : name;
      const typeName = typeToken.value;

      // Check for type member access: State.dogPage  or  State['dogPage']
      if (member || member_key) {
        const key = member
          ? member.value
          : member_key.value.slice(1, -1); // strip surrounding quotes
        baseType = new TypeMemberAccess(new TypeAlias(typeName), key);
      } else
      // Check if it's a generic type instantiation: Type<Args>
      if (type_args) {
        const typeArgs = parseTypeArguments(type_args);
        baseType = Types.generic(new TypeAlias(typeName), typeArgs);
      } else {
        baseType = primitiveFromName(typeName);
      }
    }
  }
  
  // Handle array_suffix for any base type (object, named, literal, generic)
  // array_suffix appears in children array since it has no name label in grammar
  const arraySuffixNode = typePrimaryNode.children?.find(child => child.type === 'array_suffix');
  if (arraySuffixNode) {
    return parseArraySuffix(baseType, arraySuffixNode);
  }
  
  return baseType;
}

/**
 * Recursively collect element types from a tuple_type_elements AST node.
 * Grammar: element:type_expression (', ' rest:tuple_type_elements)?
 * @param {Object|null} node - tuple_type_elements AST node
 * @returns {Type[]}
 */
function parseTupleTypeElements(node) {
  if (!node) return [];
  const types = [];
  let current = node;
  while (current) {
    if (current.named?.element) {
      types.push(parseTypeExpression(current.named.element));
    }
    current = current.named?.rest ?? null;
  }
  return types;
}

/**
 * Parse an array_suffix node to handle one or more [] pairs
 * Multi-dimensional arrays recursively wrap the type
 * @param {Type} elementType - The base type before array notation
 * @param {Object} arraySuffixNode - The array_suffix AST node
 * @returns {ArrayType}
 */
function parseArraySuffix(elementType, arraySuffixNode) {
  let result = Types.array(elementType);
  
  // array_suffix recursively contains more array_suffix nodes in children array
  // Grammar: 'array_suffix': [['[', ']', 'array_suffix?']]
  const nestedArraySuffix = arraySuffixNode.children?.find(child => child.type === 'array_suffix');
  if (nestedArraySuffix) {
    // This means we have [][] or more
    return parseArraySuffix(result, nestedArraySuffix);
  }
  
  return result;
}

/**
 * Parse an object_type AST node into an ObjectType
 * @param {Object} objectTypeNode - The object_type AST node
 * @returns {ObjectType}
 */
export function parseObjectType(objectTypeNode) {
  if (!objectTypeNode || !objectTypeNode.named || !objectTypeNode.named.properties) {
    return Types.object(new Map());
  }
  
  const properties = new Map();
  let indexSignature = null;
  
  function collectProperties(node) {
    if (!node) return;
    
    // Check if this is a single property node
    if (node.type === 'object_type_property') {
      // Index signature: [keyName: keyType]: valueType
      if (node.named?.keyName) {
        indexSignature = {
          keyType: parseTypeExpression(node.named.keyType),
          valueType: parseTypeExpression(node.named.valueType),
        };
        return;
      }

      const key = node.named.key?.value;
      const optional = !!node.named.optional;
      const valueType = parseTypeExpression(node.named.valueType);
      
      if (key) {
        properties.set(key, { type: valueType, optional });
      }
      // Do NOT recurse into this property's children — valueType is already
      // handled by parseTypeExpression above. Recursing would hoist nested
      // object properties (e.g. score inside dogPage:{score:number}) to the
      // top level, corrupting the outer object type.
      return;
    }
    
    // Recursively process children
    if (node.children) {
      node.children.forEach(collectProperties);
    }
    
    // Recursively process named children
    if (node.named) {
      Object.values(node.named).forEach(child => {
        if (child && typeof child === 'object' && child.type) {
          collectProperties(child);
        }
      });
    }
  }
  
  collectProperties(objectTypeNode.named.properties);
  
  return Types.object(properties, indexSignature);
}

/**
 * Parse type arguments from a type_arg_list node.
 * @param {Object} typeArgsNode - The type_arg_list AST node
 * @returns {Type[]}
 */
export function parseTypeArguments(typeArgsNode) {
  if (!typeArgsNode) return [];

  const args = [];

  function collectArgs(node) {
    if (!node?.named) return;
    if (node.named.arg) {
      const typeArg = parseTypeExpression(node.named.arg);
      if (typeArg) args.push(typeArg);
    }
    if (node.named.rest) collectArgs(node.named.rest);
  }

  // typeArgsNode is the type_arg_list node itself: call collectArgs directly.
  collectArgs(typeArgsNode);
  return args;
}

/**
 * Parse generic parameters from generic_params node
 * @param {Object} genericParamsNode - The generic_params AST node
 * @returns {string[]} Array of type parameter names
 */
export function parseGenericParams(genericParamsNode) {
  if (!genericParamsNode || !genericParamsNode.named || !genericParamsNode.named.params) {
    return [];
  }
  
  const params = [];
  
  function collectParams(node) {
    if (!node) return;
    
    if (node.named && node.named.param) {
      params.push(node.named.param.value);
    }
    
    if (node.named && node.named.rest) {
      collectParams(node.named.rest);
    }
  }
  
  collectParams(genericParamsNode.named.params);
  
  return params;
}

/**
 * Parse generic parameter constraints from generic_params node.
 * Returns a Map from parameter name to its constraint type (from `T extends X`).
 * Parameters without a constraint are absent from the map.
 * @param {Object} genericParamsNode - The generic_params AST node
 * @returns {Map<string, import('./Type.js').Type>}
 */
export function parseGenericConstraints(genericParamsNode) {
  const constraints = new Map();
  if (!genericParamsNode?.named?.params) return constraints;

  function collectConstraints(node) {
    if (!node) return;
    if (node.named?.param && node.named?.constraint) {
      const constraintType = parseTypeExpression(node.named.constraint);
      if (constraintType) {
        constraints.set(node.named.param.value, constraintType);
      }
    }
    if (node.named?.rest) collectConstraints(node.named.rest);
  }

  collectConstraints(genericParamsNode.named.params);
  return constraints;
}

/**
 * Convert a type name string to a Type object
 * Handles primitive types, boolean literals, and type alias references
 * @param {string} name - Type name
 * @returns {Type}
 */
export function primitiveFromName(name) {
  switch (name) {
    case 'string': return StringType;
    case 'number': return NumberType;
    case 'boolean': return BooleanType;
    case 'null': return NullType;
    case 'undefined': return UndefinedType;
    case 'any': return Types.any;
    case 'never': return Types.never;
    case 'void': return Types.void;
    case 'function': return AnyFunctionType;
    case 'true': return Types.literal(true, BooleanType);
    case 'false': return Types.literal(false, BooleanType);
    default:
      // Unknown type - treat as type alias reference
      return new TypeAlias(name);
  }
}

/**
 * Get the base type of a literal type (for widening)
 * @param {Type} type
 * @returns {Type}
 */
export function getBaseType(type) {
  if (type instanceof LiteralType) {
    return type.baseType;
  }
  return type;
}
